import React, { useEffect, useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import clsx from 'clsx'
import { useAtom, useSetAtom } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import PageHeader from '@node-space/storybook-components/dist/PageHeader'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import {
  Box,
  Button,
  Box as ContactInfoContainer,
  Box as Container,
  Icon,
  Text,
} from '@node-space/ui-components'
import { Description } from 'components/text/Description'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useContactInstrumentQuery } from 'hooks/queries/useContactInstrumentsQuery'
import { useContactQuery } from 'hooks/queries/useContactQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ActionIds } from 'types/types'
import { contactAtom } from './atoms/contactAtoms'
import { ContactInfo } from './components/ContactInfo'
import { ContactsSheets } from './components/ContactsSheets'
import { ContactInstrumentsList } from './ContactInstrumentsList'
import { ContactsListSkeletonLoader } from './ContactsListSkeletonLoader'

export const ContractInstrument = () => {
  const { t } = useTranslation()
  const { viewOnly } = useProfileContext() || {}
  const permissions = useMappedRoles()?.contacts
  const canEdit = permissions?.canEdit
  const { id }: { id?: number } = useParams()
  const { data: contact, isError: isContactError } = useContactQuery(id?.toString())
  const {
    data: { content: instrumentsList } = {},
    isFetching,
    isError,
  } = useContactInstrumentQuery({
    contactId: id?.toString(),
  })
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const [action, setAction] = useAtom(actionAtom)
  const setContact = useSetAtom(contactAtom)

  useEffect(() => {
    if (!isEmpty(contact)) {
      setContact(contact)
    }
  }, [contact])

  const triggerOpenSheet = () => {
    setContact(contact)
    setAction({
      ...action,
      actionId: ActionIds?.INSTRUMENT,
      header: t('contacts.addCryptoContact'),
      onClose: () => {
        setIsSheetOpen(false)
      },
    })
    setIsSheetOpen(true)
  }

  return (
    <>
      <PageHeader
        description={
          <Text color="subdued">
            {t('contacts.contactDetailsDescription', { contactName: contact?.name })}
          </Text>
        }
        renderRightSection={
          canEdit &&
          !isFetching &&
          !!instrumentsList?.length && (
            <Button
              testId="add-contact-instrument"
              variant="secondary"
              disabled={viewOnly}
              onClick={triggerOpenSheet}
              iconElement={<Icon name="Plus" />}
            >
              {t('contacts.cryptoAccount')}
            </Button>
          )
        }
      />

      <Container flexCol className="md:flex-row" gapX={24} gapY={24} width="full">
        <Box
          flexCol
          width="full"
          className={clsx({
            'md:w-2/3 break-words order-2 md:order-none': !isError && !isFetching,
          })}
        >
          {/* Show error state */}
          {isError && !isFetching && (
            <StatusCard status="error" headline={t('defaultError')}>
              <Description>
                {`${t('requestErrors.comeBackLater')} `}
                <TextAction isWindowReload text={t('requestErrors.refreshPage')} />.
              </Description>
            </StatusCard>
          )}

          {/* Show content state */}
          {!isError && (
            <>
              {isFetching && <ContactsListSkeletonLoader />}

              {!!instrumentsList?.length && !isFetching && (
                <Box className="w-full">
                  <ContactInstrumentsList
                    contactInstruments={instrumentsList}
                    setIsSheetOpen={setIsSheetOpen}
                  />
                </Box>
              )}
            </>
          )}

          {/* Show empty state */}
          {!isFetching && !isError && !instrumentsList?.length && (
            <StatusCard
              headline={t('contacts.noContactInstrumentHeadline', {
                contactName: contact?.name,
              })}
              description={t('contacts.addContactInstrumentDescription')}
              cardButton={
                canEdit && {
                  testid: 'add-contact-instrument',
                  disabled: viewOnly,
                  onClick: triggerOpenSheet,
                  iconElement: <Icon name="Plus" />,
                  name: t('contacts.cryptoAccount'),
                }
              }
            />
          )}
        </Box>

        {!isEmpty(contact) && !isContactError && (
          <ContactInfoContainer flexCol className="md:w-1/3 order-1 md:order-none">
            <ContactInfo />
          </ContactInfoContainer>
        )}
      </Container>
      <ContactsSheets isSheetOpen={isSheetOpen} />
    </>
  )
}
